import React, { useState, useRef, useId } from 'react';
import { config, features } from '../config';

interface FileUploadProps {
  onFileSelect: (file: File) => void;
  disabled?: boolean;
  multiple?: boolean;
}

export const FileUpload: React.FC<FileUploadProps> = ({ onFileSelect, disabled, multiple = false }) => {
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const inputId = useId();

  const validateFile = (file: File): boolean => {
    
    // Check file type
    if (!config.supportedFileTypes.includes(file.type)) {
      alert(`Unsupported file type. Please upload: ${config.supportedFileTypes.join(', ')}`);
      return false;
    }

    // Check file size
    if (file.size > config.maxFileSize) {
      const maxSizeMB = (config.maxFileSize / 1024 / 1024).toFixed(1);
      alert(`File too large. Maximum size: ${maxSizeMB}MB`);
      return false;
    }

    return true;
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      // Process each file
      Array.from(files).forEach(file => {
        if (validateFile(file)) {
          onFileSelect(file);
        }
      });
      // Clear the input value to allow re-selection of the same file
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(false);
    
    const files = event.dataTransfer.files;
    if (files) {
      // Process each file
      Array.from(files).forEach(file => {
        if (validateFile(file)) {
          onFileSelect(file);
        }
      });
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    if (features.enableDragDrop) {
      setDragOver(true);
    }
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  return (
    <div
      className={`file-upload ${dragOver ? 'drag-over' : ''} ${disabled ? 'disabled' : ''}`}
      onDrop={features.enableDragDrop ? handleDrop : undefined}
      onDragOver={features.enableDragDrop ? handleDragOver : undefined}
      onDragLeave={features.enableDragDrop ? handleDragLeave : undefined}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept={config.supportedFileTypes.join(',')}
        onChange={handleFileChange}
        disabled={disabled}
        id={inputId}
        multiple={multiple}
      />
      <label htmlFor={inputId}>
        <div className="upload-content">
          <div className="upload-icon">🧾</div>
          <div className="upload-text">
            <strong>Choose receipt image{multiple ? 's' : ''}</strong>
            {features.enableDragDrop && ' or drag them here'}
          </div>
          <div className="upload-subtext">
            Image files (JPG, PNG, GIF, WEBP) • Max size: {(config.maxFileSize / 1024 / 1024).toFixed(0)}MB
            {multiple && ' • Select multiple files'}
          </div>
        </div>
      </label>
    </div>
  );
};
